import React from "react";
import { ThreeEvent } from "@react-three/fiber";

const CLICK_MOVE_THRESHOLD_PX = 2;

type PointerSelectionState = {
  pointerId: number;
  clientX: number;
  clientY: number;
  moved: boolean;
};

export function usePointerUpSelection<T>(
  target: T,
  onSelect: (target: T, event: ThreeEvent<PointerEvent>) => void,
  options: { claimPointerEvents?: boolean } = {},
) {
  const { claimPointerEvents = false } = options;
  const pointerStateRef = React.useRef<PointerSelectionState | null>(null);

  const handlePointerDown = React.useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      if (event.button !== 0) return;
      if (claimPointerEvents) {
        claimPointerEvent(event);
      }

      pointerStateRef.current = {
        pointerId: event.pointerId,
        clientX: event.clientX,
        clientY: event.clientY,
        moved: false,
      };
    },
    [claimPointerEvents],
  );

  const handlePointerMove = React.useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      const pointerState = pointerStateRef.current;
      if (!pointerState || pointerState.pointerId !== event.pointerId) return;
      if (claimPointerEvents) {
        claimPointerEvent(event);
      }

      if (hasPointerMoved(pointerState, event)) {
        pointerState.moved = true;
      }
    },
    [claimPointerEvents],
  );

  const handlePointerUp = React.useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      const pointerState = pointerStateRef.current;
      if (!pointerState || pointerState.pointerId !== event.pointerId) return;
      if (claimPointerEvents) {
        claimPointerEvent(event);
      }

      pointerStateRef.current = null;
      if (pointerState.moved || hasPointerMoved(pointerState, event)) return;

      onSelect(target, event);
    },
    [claimPointerEvents, onSelect, target],
  );

  return {
    onPointerDown: handlePointerDown,
    onPointerMove: handlePointerMove,
    onPointerUp: handlePointerUp,
  };
}

function hasPointerMoved(
  pointerState: PointerSelectionState,
  event: ThreeEvent<PointerEvent>,
): boolean {
  return (
    Math.abs(event.clientX - pointerState.clientX) > CLICK_MOVE_THRESHOLD_PX ||
    Math.abs(event.clientY - pointerState.clientY) > CLICK_MOVE_THRESHOLD_PX
  );
}

function claimPointerEvent(event: ThreeEvent<PointerEvent>) {
  event.stopPropagation();
  event.nativeEvent.stopImmediatePropagation?.();
}
